import { useState, useEffect } from "react";
import { useAuth, API } from "@/App";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { Badge } from "@/components/ui/badge";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { toast } from "sonner";
import { 
  Truck, Package, Clock, MapPin, Phone, User, AlertTriangle, 
  ThermometerSun, Plus, Search, Filter, Eye,
  CheckCircle, XCircle, Timer, Navigation, Droplet, Heart,
  Pill, TestTube, Biohazard, ShieldAlert, ShoppingCart
} from "lucide-react";

// Icon mapping for cargo types
const cargoIcons = {
  "package": Package,
  "shopping-cart": ShoppingCart,
  "pill": Pill,
  "test-tube": TestTube,
  "droplet": Droplet,
  "heart": Heart,
  "shield-alert": ShieldAlert,
  "biohazard": Biohazard
};

// Status colors and icons
const statusConfig = {
  pending: { color: "bg-yellow-100 text-yellow-800", icon: Clock, label: "Pending" },
  dispatched: { color: "bg-blue-100 text-blue-800", icon: Navigation, label: "Dispatched" },
  assigned: { color: "bg-purple-100 text-purple-800", icon: User, label: "Assigned" },
  accepted: { color: "bg-indigo-100 text-indigo-800", icon: CheckCircle, label: "Accepted" },
  picked_up: { color: "bg-cyan-100 text-cyan-800", icon: Package, label: "Picked Up" },
  in_transit: { color: "bg-orange-100 text-orange-800", icon: Truck, label: "In Transit" },
  delivered: { color: "bg-green-100 text-green-800", icon: CheckCircle, label: "Delivered" },
  cancelled: { color: "bg-red-100 text-red-800", icon: XCircle, label: "Cancelled" }
};

// Urgency colors
const urgencyConfig = {
  routine: { color: "bg-slate-100 text-slate-700", label: "Routine" },
  same_day: { color: "bg-blue-100 text-blue-700", label: "Same Day" },
  stat: { color: "bg-orange-100 text-orange-700", label: "STAT" },
  critical: { color: "bg-red-100 text-red-700", label: "Critical" }
};

export default function CourierJobsManagement() {
  const { token } = useAuth();
  const [jobs, setJobs] = useState([]);
  const [cargoTypes, setCargoTypes] = useState({});
  const [certifications, setCertifications] = useState({});
  const [urgencyTiers, setUrgencyTiers] = useState({});
  const [couriers, setCouriers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [selectedJob, setSelectedJob] = useState(null);
  const [filterStatus, setFilterStatus] = useState("all");
  const [filterUrgency, setFilterUrgency] = useState("all");
  const [searchTerm, setSearchTerm] = useState("");

  // Form state for new job
  const [newJob, setNewJob] = useState({
    pickup_facility_name: "",
    pickup_address: "",
    pickup_contact_name: "",
    pickup_contact_phone: "",
    pickup_instructions: "",
    delivery_facility_name: "",
    delivery_address: "",
    delivery_contact_name: "",
    delivery_contact_phone: "",
    delivery_instructions: "",
    cargo_type: "medical_supplies",
    cargo_description: "",
    cargo_quantity: 1,
    cargo_weight_lbs: "",
    requires_signature: true,
    requires_photo_proof: true,
    temperature_requirements: "ambient",
    urgency: "routine",
    requested_pickup_time: "",
    requested_delivery_time: "",
    reference_type: "",
    reference_id: "",
    notes: ""
  });

  useEffect(() => {
    fetchCargoTypes();
    fetchJobs();
    fetchCouriers();
  }, []);

  const fetchCargoTypes = async () => {
    try {
      const res = await fetch(`${API}/couriers/cargo-types`);
      const data = await res.json();
      setCargoTypes(data.cargo_types || {});
      setCertifications(data.certifications || {});
      setUrgencyTiers(data.urgency_tiers || {});
    } catch (err) {
      console.error("Failed to fetch cargo types:", err);
    }
  };

  const fetchJobs = async () => {
    try {
      const res = await fetch(`${API}/courier-jobs`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      const data = await res.json();
      setJobs(data.jobs || []);
    } catch (err) {
      console.error("Failed to fetch courier jobs:", err);
      toast.error("Failed to load courier jobs");
    } finally {
      setLoading(false);
    }
  };

  const fetchCouriers = async () => {
    try {
      const res = await fetch(`${API}/couriers/available`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      const data = await res.json();
      setCouriers(data.couriers || []);
    } catch (err) {
      console.error("Failed to fetch couriers:", err);
    }
  };

  const createJob = async () => {
    try {
      const res = await fetch(`${API}/courier-jobs`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`
        },
        body: JSON.stringify({
          ...newJob,
          cargo_weight_lbs: newJob.cargo_weight_lbs ? parseFloat(newJob.cargo_weight_lbs) : null
        })
      });
      
      if (res.ok) {
        const data = await res.json();
        toast.success(`Courier job ${data.job.job_number} created`);
        setShowCreateModal(false);
        fetchJobs();
        resetForm();
      } else {
        const error = await res.json();
        toast.error(error.detail || "Failed to create job");
      }
    } catch (err) {
      toast.error("Failed to create courier job");
    }
  };

  const resetForm = () => {
    setNewJob({
      pickup_facility_name: "",
      pickup_address: "",
      pickup_contact_name: "",
      pickup_contact_phone: "",
      pickup_instructions: "",
      delivery_facility_name: "",
      delivery_address: "",
      delivery_contact_name: "",
      delivery_contact_phone: "",
      delivery_instructions: "",
      cargo_type: "medical_supplies",
      cargo_description: "",
      cargo_quantity: 1,
      cargo_weight_lbs: "",
      requires_signature: true,
      requires_photo_proof: true,
      temperature_requirements: "ambient",
      urgency: "routine",
      requested_pickup_time: "",
      requested_delivery_time: "",
      reference_type: "",
      reference_id: "",
      notes: ""
    });
  };

  const dispatchJob = async (jobId, courierIds) => {
    try {
      const res = await fetch(`${API}/courier-jobs/${jobId}/dispatch`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`
        },
        body: JSON.stringify(courierIds)
      });
      
      if (res.ok) {
        const data = await res.json();
        toast.success(data.message);
        fetchJobs();
        setSelectedJob(null);
      } else {
        const error = await res.json();
        toast.error(error.detail?.message || error.detail || "Failed to dispatch job");
      }
    } catch (err) {
      toast.error("Failed to dispatch job");
    }
  };

  const filteredJobs = jobs.filter(job => {
    if (filterStatus !== "all" && job.status !== filterStatus) return false;
    if (filterUrgency !== "all" && job.urgency?.tier !== filterUrgency) return false;
    if (searchTerm) {
      const search = searchTerm.toLowerCase();
      return (
        job.job_number?.toLowerCase().includes(search) ||
        job.pickup?.facility_name?.toLowerCase().includes(search) ||
        job.delivery?.facility_name?.toLowerCase().includes(search) ||
        job.cargo?.description?.toLowerCase().includes(search)
      );
    }
    return true;
  });

  const getCargoIcon = (iconName) => {
    const Icon = cargoIcons[iconName] || Package;
    return <Icon className="w-4 h-4" />;
  };

  return (
    <div data-testid="courier-jobs-management">
      {/* Header */}
      <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4 mb-6">
        <div>
          <h1 className="text-2xl font-bold text-slate-900 flex items-center gap-3">
            <Truck className="w-7 h-7 text-blue-600" />
            Med Courier Jobs
          </h1>
          <p className="text-slate-600 mt-1">Manage medical delivery and logistics jobs</p>
        </div>
        
        <Dialog open={showCreateModal} onOpenChange={setShowCreateModal}>
          <DialogTrigger asChild>
            <Button className="bg-blue-600 hover:bg-blue-700" data-testid="create-courier-job-btn">
              <Plus className="w-4 h-4 mr-2" />
              New Courier Job
            </Button>
          </DialogTrigger>
          <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2">
                <Truck className="w-5 h-5 text-blue-600" />
                Create Courier Job
              </DialogTitle>
            </DialogHeader>
            
            <div className="space-y-6 py-4">
              {/* Cargo Details */}
              <div className="space-y-4">
                <h3 className="font-semibold text-slate-900 flex items-center gap-2">
                  <Package className="w-4 h-4" />
                  Cargo Details
                </h3>
                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label>Cargo Type *</Label>
                    <Select
                      value={newJob.cargo_type}
                      onValueChange={(v) => setNewJob({...newJob, cargo_type: v})}
                    >
                      <SelectTrigger data-testid="cargo-type-select">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        {Object.entries(cargoTypes).map(([key, cargo]) => (
                          <SelectItem key={key} value={key}>
                            <div className="flex items-center gap-2">
                              {getCargoIcon(cargo.icon)}
                              {cargo.name}
                            </div>
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                    {cargoTypes[newJob.cargo_type]?.certifications_required?.length > 0 && (
                      <p className="text-xs text-orange-600 flex items-center gap-1">
                        <AlertTriangle className="w-3 h-3" />
                        Requires: {cargoTypes[newJob.cargo_type].certifications_required.join(", ")}
                      </p>
                    )}
                  </div>
                  <div className="space-y-2">
                    <Label>Urgency *</Label>
                    <Select
                      value={newJob.urgency}
                      onValueChange={(v) => setNewJob({...newJob, urgency: v})}
                    >
                      <SelectTrigger data-testid="urgency-select">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        {Object.entries(urgencyTiers).map(([key, tier]) => (
                          <SelectItem key={key} value={key}>
                            <div className="flex items-center gap-2">
                              <Timer className="w-4 h-4" />
                              {tier.name} ({tier.max_hours}h max)
                            </div>
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                </div>
                <div className="space-y-2">
                  <Label>Cargo Description *</Label>
                  <Textarea
                    value={newJob.cargo_description}
                    onChange={(e) => setNewJob({...newJob, cargo_description: e.target.value})}
                    placeholder="Describe the cargo contents..."
                    data-testid="cargo-description-input"
                  />
                </div>
                <div className="grid grid-cols-3 gap-4">
                  <div className="space-y-2">
                    <Label>Quantity</Label>
                    <Input
                      type="number"
                      value={newJob.cargo_quantity}
                      onChange={(e) => setNewJob({...newJob, cargo_quantity: parseInt(e.target.value) || 1})}
                    />
                  </div>
                  <div className="space-y-2">
                    <Label>Weight (lbs)</Label>
                    <Input
                      type="number"
                      value={newJob.cargo_weight_lbs}
                      onChange={(e) => setNewJob({...newJob, cargo_weight_lbs: e.target.value})}
                      placeholder="Optional"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label>Temperature</Label>
                    <Select
                      value={newJob.temperature_requirements}
                      onValueChange={(v) => setNewJob({...newJob, temperature_requirements: v})}
                    >
                      <SelectTrigger>
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="ambient">Ambient</SelectItem>
                        <SelectItem value="refrigerated">Refrigerated (2-8°C)</SelectItem>
                        <SelectItem value="frozen">Frozen (-20°C)</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                </div>
              </div>

              {/* Pickup Info */}
              <div className="space-y-4 p-4 bg-green-50 rounded-lg">
                <h3 className="font-semibold text-green-800 flex items-center gap-2">
                  <MapPin className="w-4 h-4" />
                  Pickup Location
                </h3>
                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label>Facility Name *</Label>
                    <Input
                      value={newJob.pickup_facility_name}
                      onChange={(e) => setNewJob({...newJob, pickup_facility_name: e.target.value})}
                      placeholder="Hospital/Clinic name"
                      data-testid="pickup-facility-input"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label>Contact Name *</Label>
                    <Input
                      value={newJob.pickup_contact_name}
                      onChange={(e) => setNewJob({...newJob, pickup_contact_name: e.target.value})}
                      placeholder="Contact person"
                    />
                  </div>
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label>Address *</Label>
                    <Input
                      value={newJob.pickup_address}
                      onChange={(e) => setNewJob({...newJob, pickup_address: e.target.value})}
                      placeholder="Full address"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label>Phone *</Label>
                    <Input
                      value={newJob.pickup_contact_phone}
                      onChange={(e) => setNewJob({...newJob, pickup_contact_phone: e.target.value})}
                      placeholder="Contact phone"
                    />
                  </div>
                </div>
                <div className="space-y-2">
                  <Label>Pickup Instructions</Label>
                  <Textarea
                    value={newJob.pickup_instructions}
                    onChange={(e) => setNewJob({...newJob, pickup_instructions: e.target.value})}
                    placeholder="Special instructions for pickup..."
                    rows={2}
                  />
                </div>
              </div>

              {/* Delivery Info */}
              <div className="space-y-4 p-4 bg-blue-50 rounded-lg">
                <h3 className="font-semibold text-blue-800 flex items-center gap-2">
                  <Navigation className="w-4 h-4" />
                  Delivery Location
                </h3>
                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label>Facility Name *</Label>
                    <Input
                      value={newJob.delivery_facility_name}
                      onChange={(e) => setNewJob({...newJob, delivery_facility_name: e.target.value})}
                      placeholder="Hospital/Clinic name"
                      data-testid="delivery-facility-input"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label>Contact Name *</Label>
                    <Input
                      value={newJob.delivery_contact_name}
                      onChange={(e) => setNewJob({...newJob, delivery_contact_name: e.target.value})}
                      placeholder="Contact person"
                    />
                  </div>
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label>Address *</Label>
                    <Input
                      value={newJob.delivery_address}
                      onChange={(e) => setNewJob({...newJob, delivery_address: e.target.value})}
                      placeholder="Full address"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label>Phone *</Label>
                    <Input
                      value={newJob.delivery_contact_phone}
                      onChange={(e) => setNewJob({...newJob, delivery_contact_phone: e.target.value})}
                      placeholder="Contact phone"
                    />
                  </div>
                </div>
                <div className="space-y-2">
                  <Label>Delivery Instructions</Label>
                  <Textarea
                    value={newJob.delivery_instructions}
                    onChange={(e) => setNewJob({...newJob, delivery_instructions: e.target.value})}
                    placeholder="Special instructions for delivery..."
                    rows={2}
                  />
                </div>
              </div>

              {/* Notes */}
              <div className="space-y-2">
                <Label>Additional Notes</Label>
                <Textarea
                  value={newJob.notes}
                  onChange={(e) => setNewJob({...newJob, notes: e.target.value})}
                  placeholder="Any additional information..."
                  rows={2}
                />
              </div>

              <div className="flex justify-end gap-3 pt-4 border-t">
                <Button variant="outline" onClick={() => setShowCreateModal(false)}>
                  Cancel
                </Button>
                <Button 
                  onClick={createJob}
                  disabled={!newJob.pickup_facility_name || !newJob.delivery_facility_name || !newJob.cargo_description}
                  className="bg-blue-600 hover:bg-blue-700"
                  data-testid="submit-courier-job-btn"
                >
                  Create Job
                </Button>
              </div>
            </div>
          </DialogContent>
        </Dialog>
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
        <Card className="bg-white">
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-yellow-100 rounded-lg">
                <Clock className="w-5 h-5 text-yellow-600" />
              </div>
              <div>
                <p className="text-2xl font-bold text-slate-900">
                  {jobs.filter(j => j.status === "pending").length}
                </p>
                <p className="text-sm text-slate-600">Pending</p>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card className="bg-white">
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-orange-100 rounded-lg">
                <Truck className="w-5 h-5 text-orange-600" />
              </div>
              <div>
                <p className="text-2xl font-bold text-slate-900">
                  {jobs.filter(j => ["in_transit", "picked_up"].includes(j.status)).length}
                </p>
                <p className="text-sm text-slate-600">In Transit</p>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card className="bg-white">
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-red-100 rounded-lg">
                <AlertTriangle className="w-5 h-5 text-red-600" />
              </div>
              <div>
                <p className="text-2xl font-bold text-slate-900">
                  {jobs.filter(j => ["stat", "critical"].includes(j.urgency?.tier)).length}
                </p>
                <p className="text-sm text-slate-600">STAT/Critical</p>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card className="bg-white">
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-green-100 rounded-lg">
                <CheckCircle className="w-5 h-5 text-green-600" />
              </div>
              <div>
                <p className="text-2xl font-bold text-slate-900">
                  {jobs.filter(j => j.status === "delivered").length}
                </p>
                <p className="text-sm text-slate-600">Delivered</p>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Filters */}
      <Card className="mb-6">
        <CardContent className="p-4">
          <div className="flex flex-wrap gap-4 items-center">
            <div className="flex-1 min-w-[200px]">
              <div className="relative">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                <Input
                  placeholder="Search jobs..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="pl-10"
                  data-testid="search-jobs-input"
                />
              </div>
            </div>
            <Select value={filterStatus} onValueChange={setFilterStatus}>
              <SelectTrigger className="w-[160px]">
                <Filter className="w-4 h-4 mr-2" />
                <SelectValue placeholder="Status" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Status</SelectItem>
                {Object.entries(statusConfig).map(([key, config]) => (
                  <SelectItem key={key} value={key}>{config.label}</SelectItem>
                ))}
              </SelectContent>
            </Select>
            <Select value={filterUrgency} onValueChange={setFilterUrgency}>
              <SelectTrigger className="w-[160px]">
                <Timer className="w-4 h-4 mr-2" />
                <SelectValue placeholder="Urgency" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Urgency</SelectItem>
                {Object.entries(urgencyConfig).map(([key, config]) => (
                  <SelectItem key={key} value={key}>{config.label}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        </CardContent>
      </Card>

      {/* Jobs List */}
      {loading ? (
        <div className="text-center py-12">
          <div className="animate-spin w-8 h-8 border-4 border-blue-600 border-t-transparent rounded-full mx-auto"></div>
          <p className="mt-4 text-slate-600">Loading courier jobs...</p>
        </div>
      ) : filteredJobs.length === 0 ? (
        <Card className="text-center py-12">
          <CardContent>
            <Truck className="w-16 h-16 text-slate-300 mx-auto mb-4" />
            <h3 className="text-lg font-semibold text-slate-900 mb-2">No Courier Jobs</h3>
            <p className="text-slate-600 mb-4">Create your first courier job to get started</p>
            <Button onClick={() => setShowCreateModal(true)} className="bg-blue-600 hover:bg-blue-700">
              <Plus className="w-4 h-4 mr-2" />
              Create Job
            </Button>
          </CardContent>
        </Card>
      ) : (
        <div className="space-y-4">
          {filteredJobs.map((job) => {
            const status = statusConfig[job.status] || statusConfig.pending;
            const urgency = urgencyConfig[job.urgency?.tier] || urgencyConfig.routine;
            const StatusIcon = status.icon;
            const cargoConfig = cargoTypes[job.cargo?.type] || {};
            
            return (
              <Card 
                key={job.id} 
                className="hover:shadow-lg transition-shadow cursor-pointer"
                onClick={() => setSelectedJob(job)}
                data-testid={`courier-job-${job.job_number}`}
              >
                <CardContent className="p-4">
                  <div className="flex items-start gap-4">
                    {/* Cargo Icon */}
                    <div className={`p-3 rounded-xl ${job.cargo?.is_biohazard ? 'bg-red-100' : 'bg-blue-100'}`}>
                      {getCargoIcon(cargoConfig.icon)}
                    </div>
                    
                    {/* Job Info */}
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 mb-1 flex-wrap">
                        <span className="font-bold text-slate-900">{job.job_number}</span>
                        <Badge className={status.color}>
                          <StatusIcon className="w-3 h-3 mr-1" />
                          {status.label}
                        </Badge>
                        <Badge className={urgency.color}>
                          {urgency.label}
                        </Badge>
                        {job.cargo?.is_temperature_controlled && (
                          <Badge variant="outline" className="text-cyan-600 border-cyan-300">
                            <ThermometerSun className="w-3 h-3 mr-1" />
                            Temp Controlled
                          </Badge>
                        )}
                      </div>
                      
                      <p className="text-sm text-slate-600 mb-2">
                        {job.cargo?.type_name}: {job.cargo?.description?.substring(0, 100)}
                        {job.cargo?.description?.length > 100 && "..."}
                      </p>
                      
                      <div className="flex flex-wrap gap-4 text-sm">
                        <div className="flex items-center gap-1 text-green-700">
                          <MapPin className="w-4 h-4" />
                          <span className="font-medium">From:</span>
                          <span>{job.pickup?.facility_name}</span>
                        </div>
                        <div className="flex items-center gap-1 text-blue-700">
                          <Navigation className="w-4 h-4" />
                          <span className="font-medium">To:</span>
                          <span>{job.delivery?.facility_name}</span>
                        </div>
                      </div>
                      
                      {job.courier_name && (
                        <div className="mt-2 flex items-center gap-1 text-sm text-purple-700">
                          <User className="w-4 h-4" />
                          <span className="font-medium">Courier:</span>
                          <span>{job.courier_name}</span>
                        </div>
                      )}
                    </div>
                    
                    {/* Actions */}
                    <div className="flex flex-col items-end gap-2">
                      <span className="text-xs text-slate-500">
                        {new Date(job.created_at).toLocaleDateString()}
                      </span>
                      <Button variant="ghost" size="sm">
                        <Eye className="w-4 h-4" />
                      </Button>
                    </div>
                  </div>
                </CardContent>
              </Card>
            );
          })}
        </div>
      )}

      {/* Job Detail Modal */}
      <Dialog open={!!selectedJob} onOpenChange={() => setSelectedJob(null)}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          {selectedJob && (
            <>
              <DialogHeader>
                <DialogTitle className="flex items-center gap-2">
                  <Truck className="w-5 h-5 text-blue-600" />
                  {selectedJob.job_number}
                </DialogTitle>
              </DialogHeader>
              
              <Tabs defaultValue="details" className="mt-4">
                <TabsList className="grid grid-cols-3">
                  <TabsTrigger value="details">Details</TabsTrigger>
                  <TabsTrigger value="tracking">Tracking</TabsTrigger>
                  <TabsTrigger value="dispatch">Dispatch</TabsTrigger>
                </TabsList>
                
                <TabsContent value="details" className="space-y-4 mt-4">
                  {/* Status & Urgency */}
                  <div className="flex gap-2">
                    <Badge className={statusConfig[selectedJob.status]?.color}>
                      {statusConfig[selectedJob.status]?.label}
                    </Badge>
                    <Badge className={urgencyConfig[selectedJob.urgency?.tier]?.color}>
                      {selectedJob.urgency?.name}
                    </Badge>
                  </div>
                  
                  {/* Cargo */}
                  <Card>
                    <CardHeader className="pb-2">
                      <CardTitle className="text-sm flex items-center gap-2">
                        <Package className="w-4 h-4" />
                        Cargo Details
                      </CardTitle>
                    </CardHeader>
                    <CardContent className="text-sm space-y-2">
                      <p><strong>Type:</strong> {selectedJob.cargo?.type_name}</p>
                      <p><strong>Description:</strong> {selectedJob.cargo?.description}</p>
                      <p><strong>Quantity:</strong> {selectedJob.cargo?.quantity}</p>
                      {selectedJob.cargo?.weight_lbs && (
                        <p><strong>Weight:</strong> {selectedJob.cargo.weight_lbs} lbs</p>
                      )}
                      {selectedJob.cargo?.certifications_required?.length > 0 && (
                        <p className="text-orange-600">
                          <strong>Required Certs:</strong> {selectedJob.cargo.certifications_required.join(", ")}
                        </p>
                      )}
                    </CardContent>
                  </Card>
                  
                  {/* Pickup */}
                  <Card className="bg-green-50">
                    <CardHeader className="pb-2">
                      <CardTitle className="text-sm flex items-center gap-2 text-green-800">
                        <MapPin className="w-4 h-4" />
                        Pickup
                      </CardTitle>
                    </CardHeader>
                    <CardContent className="text-sm space-y-1">
                      <p><strong>{selectedJob.pickup?.facility_name}</strong></p>
                      <p>{selectedJob.pickup?.address}</p>
                      <p>{selectedJob.pickup?.contact_name} - {selectedJob.pickup?.contact_phone}</p>
                      {selectedJob.pickup?.instructions && (
                        <p className="text-slate-600 italic">{selectedJob.pickup.instructions}</p>
                      )}
                      {selectedJob.pickup?.actual_time && (
                        <p className="text-green-700 font-medium">
                          Picked up: {new Date(selectedJob.pickup.actual_time).toLocaleString()}
                        </p>
                      )}
                    </CardContent>
                  </Card>
                  
                  {/* Delivery */}
                  <Card className="bg-blue-50">
                    <CardHeader className="pb-2">
                      <CardTitle className="text-sm flex items-center gap-2 text-blue-800">
                        <Navigation className="w-4 h-4" />
                        Delivery
                      </CardTitle>
                    </CardHeader>
                    <CardContent className="text-sm space-y-1">
                      <p><strong>{selectedJob.delivery?.facility_name}</strong></p>
                      <p>{selectedJob.delivery?.address}</p>
                      <p>{selectedJob.delivery?.contact_name} - {selectedJob.delivery?.contact_phone}</p>
                      {selectedJob.delivery?.instructions && (
                        <p className="text-slate-600 italic">{selectedJob.delivery.instructions}</p>
                      )}
                      {selectedJob.delivery?.actual_time && (
                        <p className="text-blue-700 font-medium">
                          Delivered: {new Date(selectedJob.delivery.actual_time).toLocaleString()}
                        </p>
                      )}
                    </CardContent>
                  </Card>
                </TabsContent>
                
                <TabsContent value="tracking" className="mt-4">
                  <div className="space-y-3">
                    {selectedJob.tracking_updates?.length > 0 ? (
                      selectedJob.tracking_updates.map((update, idx) => (
                        <div key={idx} className="flex gap-3 p-3 bg-slate-50 rounded-lg">
                          <div className="w-2 h-2 mt-2 rounded-full bg-blue-600"></div>
                          <div>
                            <p className="font-medium text-slate-900">{update.status}</p>
                            <p className="text-sm text-slate-600">{update.notes}</p>
                            <p className="text-xs text-slate-400">
                              {new Date(update.time).toLocaleString()}
                            </p>
                          </div>
                        </div>
                      ))
                    ) : (
                      <p className="text-center text-slate-500 py-8">No tracking updates yet</p>
                    )}
                  </div>
                </TabsContent>
                
                <TabsContent value="dispatch" className="mt-4">
                  {selectedJob.status === "pending" ? (
                    <div className="space-y-4">
                      <p className="text-sm text-slate-600">
                        Select couriers to dispatch this job to. Only couriers with the required certifications will be shown.
                      </p>
                      {couriers.length > 0 ? (
                        <div className="space-y-2">
                          {couriers.map(courier => (
                            <div key={courier.id} className="flex items-center justify-between p-3 bg-slate-50 rounded-lg">
                              <div>
                                <p className="font-medium">{courier.name}</p>
                                <p className="text-sm text-slate-600">{courier.company_name}</p>
                              </div>
                              <Button 
                                size="sm"
                                onClick={(e) => {
                                  e.stopPropagation();
                                  dispatchJob(selectedJob.id, [courier.id]);
                                }}
                                data-testid={`dispatch-to-${courier.id}`}
                              >
                                Dispatch
                              </Button>
                            </div>
                          ))}
                        </div>
                      ) : (
                        <p className="text-center text-slate-500 py-8">
                          No qualified couriers available for this cargo type
                        </p>
                      )}
                    </div>
                  ) : selectedJob.courier_name ? (
                    <div className="text-center py-8">
                      <User className="w-12 h-12 text-purple-600 mx-auto mb-3" />
                      <p className="font-medium text-slate-900">Assigned to</p>
                      <p className="text-lg text-purple-600">{selectedJob.courier_name}</p>
                    </div>
                  ) : (
                    <p className="text-center text-slate-500 py-8">
                      Job is {selectedJob.status} - dispatch not available
                    </p>
                  )}
                </TabsContent>
              </Tabs>
            </>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
